import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useApolloClient } from 'react-apollo';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';

import Button from '../Button';
import { AccountPromoCodeVerify } from '../../../graphql/queries/account-referrals';
import { openAlert as openAlertAction } from '../../../actions/modal-actions';
import { getErrorString } from '../../../util/normalize-errors';
import { sanitizePromoCode } from './helpers';
import CheckboxIcon from '../../icons/CheckboxIcon';
import FormGroup from '../FormGroup';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { CURRENCY } from '../../../constants/currency';

const propTypes = {
  className: PropTypes.string,
  onPromoCodeChange: PropTypes.func,
  onPromoCodeUpdate: PropTypes.func,
  defaultPromoCode: PropTypes.string,
  accountReferralTypes: PropTypes.arrayOf(PropTypes.string),
  openAlert: PropTypes.func,
  onPromoCodeVerified: PropTypes.func,
  verifyBillingPeriod: PropTypes.bool,
  fetchPolicy: PropTypes.string,
  onDismiss: PropTypes.func,
  onDefaultPromoCodeParsed: PropTypes.func,
  price: PropTypes.number,
};

const PromoCodeInput = ({
  className,
  onPromoCodeChange,
  onPromoCodeUpdate,
  defaultPromoCode = '',
  openAlert,
  accountReferralTypes = [],
  onPromoCodeVerified,
  // Use this to ignore all billing related checks.
  // Billing shouldn't have been bundled here in the first place.
  // TODO: Separate billingPeriod logic from PromoCodeInput
  verifyBillingPeriod = true,
  billingPeriod = PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
  fetchPolicy = 'network-only',
  onDismiss,
  onDefaultPromoCodeParsed,
  price = null,
  ...props
}) => {
  const client = useApolloClient();

  const [promoCode, setPromoCode] = useState(defaultPromoCode);
  const [isApplying, setIsApplying] = useState(false);
  const [isApplied, setIsApplied] = useState(false);

  const classes = classNames({
    'promo-code-input': true,
    'has-code': promoCode?.length > 0,
    [className]: !!className,
  });

  const checkPromoCode = async () => {
    try {
      setIsApplying(true);
      setIsApplied(false);

      const {
        data: { accountPromoCodeVerify: accountPromoCode } = {},
      } = await client.query({
        query: AccountPromoCodeVerify,
        variables: {
          promoCode,
          price: price
            ? {
                value: price,
                currency: CURRENCY.USD,
              }
            : undefined,
        },
        fetchPolicy,
      });

      if (accountPromoCode.type == null) {
        throw new Error('The provided promo code is invalid');
      }

      if (!accountReferralTypes.includes(accountPromoCode.type)) {
        throw new Error('This promo code does not apply for this action.');
      }

      if (verifyBillingPeriod) {
        const accountPromoCodeBillingPeriod =
          accountPromoCode.type === ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY
            ? PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
            : PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY;

        if (
          [
            ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
            ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
            ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
          ].includes(accountPromoCode.type) &&
          accountPromoCodeBillingPeriod !== billingPeriod
        ) {
          throw new Error(
            `This promotion does not apply for a ${
              billingPeriod ===
              PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                ? 'monthly'
                : 'yearly'
            } plan. Please select a ${
              billingPeriod ===
              PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                ? 'yearly'
                : 'monthly'
            } plan instead.`,
          );
        }
      }

      if (accountPromoCode.is_valid && accountPromoCode.is_eligible) {
        onPromoCodeChange?.(promoCode);
        onPromoCodeVerified?.(accountPromoCode);
        setIsApplied(true);
        return true;
      }

      if (!accountPromoCode.is_eligible) {
        throw new Error(
          'Sorry, it looks like you do not apply for this promotion.',
        );
      } else {
        throw new Error('The provided promo code is invalid');
      }
    } catch (error) {
      openAlert({
        type: 'error',
        message: getErrorString(error),
      });
    } finally {
      setIsApplying(false);
    }

    return false;
  };

  useEffect(() => {
    if (defaultPromoCode) {
      checkPromoCode(promoCode);
      onDefaultPromoCodeParsed?.();
    }
  }, [defaultPromoCode]);

  useEffect(() => {
    onPromoCodeUpdate?.(promoCode, isApplied, checkPromoCode);
  }, [promoCode, isApplied]);

  return (
    <div className={classes}>
      <div className="promo-code-input-group">
        <FormGroup
          id="promo-code-input"
          value={promoCode}
          onChange={event => {
            setPromoCode(sanitizePromoCode(event.target.value));
          }}
          disabled={isApplying}
          label="Promo Code"
          {...props}
        />
        {onDismiss && isApplied && (
          <button
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              setPromoCode('');
              setIsApplied(false);
              onDismiss();
            }}
            className="promo-code-dismiss"
            aria-label="Dismiss"
          >
            &times;
          </button>
        )}

        <Button
          onClick={() => checkPromoCode(promoCode)}
          disabled={isApplying || !promoCode?.length}
          data-testid="promo-code-input-submit"
        >
          {isApplying ? 'Applying...' : 'Apply'}
        </Button>
      </div>

      {isApplied && (
        <span className="promo-code-success-note">
          Promo code has been applied <CheckboxIcon />
        </span>
      )}
    </div>
  );
};

PromoCodeInput.propTypes = propTypes;

export { PromoCodeInput as PromoCodeInputComponent };

const mapDispatchToProps = {
  openAlert: openAlertAction,
};

export default compose(connect(null, mapDispatchToProps))(PromoCodeInput);
